import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import { Router } from '@angular/router';
import { rxResource } from '@angular/core/rxjs-interop';
import { from } from 'rxjs';
import { LocationStateService } from '../../common/services/location-state.service';
import { Api } from '../../api/api';
import { getDevices } from '../../api/functions';
import { LocationDeviceResponse } from '../../api/models/location-device-response';

@Component({
  selector: 'app-devices',
  standalone: true,
  imports: [],
  templateUrl: './devices.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./devices.component.css']
})
export class DevicesComponent {
  private api = inject(Api);
  private locationStateService = inject(LocationStateService);
  private router = inject(Router);

  private devicesResource = rxResource<LocationDeviceResponse[], unknown>({
    defaultValue: [],
    stream: () => from(this.api.invoke(getDevices)),
  });

  filterText = signal<string>('');
  locations = computed(() =>
    [...(this.devicesResource.hasValue() ? this.devicesResource.value() : [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
  );

  filteredLocations = computed(() => {
    const filter = this.filterText().toLowerCase().trim();
    if (!filter) {
      return this.locations();
    }
    return this.locations().filter(location =>
      location.id?.toLowerCase().includes(filter) ||
      location.name?.toLowerCase().includes(filter)
    );
  });

  onFilterChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.filterText.set(input.value);
  }

  onSelectLocation(device: LocationDeviceResponse): void {
    const location = this.locationStateService.locations().find(location => location.id === device.id);
    if (location) {
      this.locationStateService.setLocation(location);
    }
    this.router.navigate(['/dashboard'], { queryParams: { locationId: device.id } });
  }

  getLocationStatus(locationId: string | undefined): 'ok' | 'error' | 'warning' {
    // Fake status - default to OK for now
    return 'ok';
  }

  formatLocationValue(value: number | undefined, unit: 'kWh' | 'kWp'): string {
    if (value === undefined || value === null) {
      return '-';
    }
    return `${value.toLocaleString('de-AT', { maximumFractionDigits: 1 })} ${unit}`;
  }
}
